import type { FacetEditorialMap } from "./types";

/** Contenu éditorial par genre (ids alignés sur FACET_REGISTRY.gender). */
export const GENDER_EDITORIAL: FacetEditorialMap = {
  women: {
    faceShapes: ["round", "square", "oval"],
    related: [
      { key: "shape", valueId: "cateye" },
      { key: "color", valueId: "gold" },
      { key: "material", valueId: "plastic" },
    ],
    fr: {
      suitsTitle: "Bien choisir ses lunettes de vue femme",
      suits:
        "Les montures femme de notre catalogue sont sélectionnées sur trois critères : une largeur adaptée aux visages plus étroits, un pont plus court pour éviter le glissement, et des styles qui ouvrent le regard (œil-de-chat, rondes, rectangulaires fines).",
      keyPoints: [
        { title: "Formes qui flattent", body: "Œil-de-chat et rondes adoucissent un visage carré ; rectangulaires fines allongent un visage rond." },
        { title: "Bon maintien", body: "Un pont trop large est la première cause de lunettes qui glissent : l'opticien le vérifie à l'essayage." },
        { title: "Verres", body: "Simple foyer, progressif, anti-lumière bleue ou solaire : toutes nos montures acceptent les quatre." },
      ],
      advice:
        "Emportez votre ancienne paire lors du test de vue : la comparaison des mesures permet de savoir immédiatement si la largeur vous convenait.",
      faq: [
        { q: "Quelles montures agrandissent le regard ?", a: "Les formes remontantes (œil-de-chat) et les cercles hauts ouvrent l'œil. Les montures très basses le referment." },
        { q: "Peut-on acheter une monture femme sans ordonnance ?", a: "Oui. Le test de vue est gratuit en boutique : notre opticien établit la prescription sur place, ou vous pouvez acheter la monture seule." },
      ],
    },
    en: {
      suitsTitle: "Choosing the right women's glasses",
      suits:
        "Women's frames in our catalogue are selected on three criteria: a width suited to narrower faces, a shorter bridge to prevent slipping, and styles that open the gaze (cat-eye, round, slim rectangle).",
      keyPoints: [
        { title: "Flattering shapes", body: "Cat-eye and round soften a square face; slim rectangles lengthen a round one." },
        { title: "Good hold", body: "A bridge that is too wide is the number one cause of sliding glasses; the optician checks it at fitting." },
        { title: "Lenses", body: "Single vision, progressive, blue-light or sun: every frame takes all four." },
      ],
      advice:
        "Bring your old pair to the eye test: comparing measurements shows immediately whether the width suited you.",
      faq: [
        { q: "Which frames make the eyes look bigger?", a: "Upswept shapes (cat-eye) and tall lenses open the eye. Very shallow frames close it." },
        { q: "Can I buy women's frames without a prescription?", a: "Yes. The eye test is free in store: our optician issues the prescription on the spot, or you can buy the frame alone." },
      ],
    },
  },

  men: {
    faceShapes: ["round", "oval", "square"],
    related: [
      { key: "shape", valueId: "rectangle" },
      { key: "material", valueId: "metal" },
      { key: "color", valueId: "black" },
    ],
    fr: {
      suitsTitle: "Bien choisir ses lunettes de vue homme",
      suits:
        "Une monture homme se choisit d'abord sur la largeur : elle doit s'arrêter au bord du visage sans comprimer les tempes. Les formes rectangulaires, carrées, aviateur et browline couvrent la quasi-totalité des morphologies.",
      keyPoints: [
        { title: "Largeur avant tout", body: "Une monture trop étroite marque les tempes en fin de journée ; trop large, elle glisse." },
        { title: "Formes polyvalentes", body: "Rectangulaire pour le bureau, aviateur ou browline pour un rendu plus affirmé." },
        { title: "Résistance", body: "Le TR90 et le métal supportent le mieux un usage physique ou en extérieur." },
      ],
      advice:
        "Si vous travaillez sur écran plus de six heures par jour, demandez un traitement anti-lumière bleue : nos opticiens l'ajoutent à n'importe quelle monture du catalogue.",
      faq: [
        { q: "Quelle monture pour un visage rond chez l'homme ?", a: "Les rectangulaires et browline structurent le mieux : leurs lignes horizontales allongent le visage." },
        { q: "Une monture large est-elle plus confortable ?", a: "Non. Le confort vient de l'adéquation entre la largeur de la monture et celle du visage, pas de la taille absolue." },
      ],
    },
    en: {
      suitsTitle: "Choosing the right men's glasses",
      suits:
        "A men's frame is chosen on width first: it should stop at the edge of the face without pressing the temples. Rectangle, square, aviator and browline shapes cover almost every face type.",
      keyPoints: [
        { title: "Width first", body: "Too narrow and the frame marks the temples by evening; too wide and it slides." },
        { title: "Versatile shapes", body: "Rectangle for the office, aviator or browline for a bolder look." },
        { title: "Durability", body: "TR90 and metal handle physical or outdoor use best." },
      ],
      advice:
        "If you work on screens more than six hours a day, ask for a blue-light coating: our opticians add it to any frame in the catalogue.",
      faq: [
        { q: "Which frame suits a round male face?", a: "Rectangle and browline structure best: their horizontal lines lengthen the face." },
        { q: "Are wider frames more comfortable?", a: "No. Comfort comes from matching frame width to face width, not from absolute size." },
      ],
    },
  },

  unisex: {
    faceShapes: ["oval", "round", "square"],
    related: [
      { key: "shape", valueId: "rectangle" },
      { key: "material", valueId: "tr" },
      { key: "color", valueId: "grey" },
    ],
    fr: {
      suitsTitle: "Pourquoi choisir une monture unisexe ?",
      suits:
        "Les montures unisexes reposent sur des proportions médianes : ni trop étroites, ni trop larges. Elles conviennent à la majorité des visages et constituent souvent le meilleur choix pour une première paire.",
      keyPoints: [
        { title: "Proportions neutres", body: "Largeur intermédiaire, pont standard : la forme la plus facile à ajuster." },
        { title: "Styles sûrs", body: "Rectangulaire, ronde, carrée et aviateur dominent la sélection." },
        { title: "Polyvalence", body: "Se portent au travail comme au quotidien, sans changer de paire." },
      ],
      advice:
        "Une monture unisexe bien ajustée doit tenir sans serrer quand vous penchez la tête en avant. C'est le test que fait l'opticien avant de valider la paire.",
      faq: [
        { q: "Une monture unisexe convient-elle vraiment à tout le monde ?", a: "À la majorité des visages, oui. La différence se joue ensuite sur la largeur totale, mesurée gratuitement en boutique." },
        { q: "Quelle matière privilégier en unisexe ?", a: "Le TR90 et l'acétate fin : légers, résistants et faciles à porter toute la journée." },
      ],
    },
    en: {
      suitsTitle: "Why choose a unisex frame?",
      suits:
        "Unisex frames use median proportions: neither too narrow nor too wide. They suit most faces and are often the best choice for a first pair.",
      keyPoints: [
        { title: "Neutral proportions", body: "Intermediate width and standard bridge: the easiest shape to fit." },
        { title: "Safe styles", body: "Rectangle, round, square and aviator dominate the selection." },
        { title: "Versatility", body: "Worn at work and every day, without switching pairs." },
      ],
      advice:
        "A well-fitted unisex frame should hold without pinching when you tilt your head forward. That is the test the optician runs before approving the pair.",
      faq: [
        { q: "Do unisex frames really suit everyone?", a: "Most faces, yes. The difference comes down to total width, measured for free in store." },
        { q: "Which material is best for unisex frames?", a: "TR90 and thin acetate: light, durable and easy to wear all day." },
      ],
    },
  },
};

/** Contenu éditorial par taille (ids alignés sur FACET_REGISTRY.size). */
export const SIZE_EDITORIAL: FacetEditorialMap = {
  small: {
    related: [
      { key: "shape", valueId: "round" },
      { key: "material", valueId: "metal" },
      { key: "gender", valueId: "women" },
    ],
    fr: {
      suitsTitle: "À qui conviennent les montures de petite taille ?",
      suits:
        "Les petites montures s'adressent aux visages étroits et aux porteurs qui veulent une paire discrète. Bien dimensionnées, elles évitent que la monture ne dépasse au-delà des tempes, ce qui est le principal défaut d'une paire trop grande.",
      keyPoints: [
        { title: "Repère", body: "La monture doit s'arrêter au bord du visage, sans le dépasser." },
        { title: "Confort", body: "Moins de matière, donc moins de poids sur le nez." },
        { title: "Verres", body: "Le petit diamètre réduit l'épaisseur des verres pour les fortes corrections." },
      ],
      advice:
        "Une paire trop petite comprime les tempes en fin de journée. Nos opticiens mesurent la largeur de votre visage gratuitement pour éviter cette erreur.",
      faq: [
        { q: "Comment savoir si j'ai besoin d'une petite monture ?", a: "Si vos anciennes lunettes dépassaient la largeur de votre visage ou glissaient sans arrêt, une taille inférieure est probablement plus juste." },
        { q: "Les petites montures conviennent-elles aux progressifs ?", a: "Il faut vérifier la hauteur du verre. Nos opticiens contrôlent ce point avant la commande." },
      ],
    },
    en: {
      suitsTitle: "Who are small frames for?",
      suits:
        "Small frames are for narrow faces and for wearers who want a discreet pair. Correctly sized, they prevent the frame from overhanging the temples, the main flaw of a pair that is too large.",
      keyPoints: [
        { title: "Rule of thumb", body: "The frame should stop at the edge of the face, not beyond." },
        { title: "Comfort", body: "Less material means less weight on the nose." },
        { title: "Lenses", body: "A small diameter reduces lens thickness for strong prescriptions." },
      ],
      advice:
        "A pair that is too small presses the temples by evening. Our opticians measure your face width for free to avoid that mistake.",
      faq: [
        { q: "How do I know if I need a small frame?", a: "If your old glasses overhung your face or kept sliding, a smaller size is probably a better fit." },
        { q: "Do small frames work with progressives?", a: "Lens height must be checked. Our opticians verify this before ordering." },
      ],
    },
  },

  medium: {
    related: [
      { key: "shape", valueId: "rectangle" },
      { key: "gender", valueId: "unisex" },
      { key: "material", valueId: "plastic" },
    ],
    fr: {
      suitsTitle: "À qui conviennent les montures de taille moyenne ?",
      suits:
        "C'est la taille standard, celle qui convient à la majorité des visages adultes. Elle offre la meilleure marge de manœuvre pour les verres progressifs et reste équilibrée sur les photos comme en visioconférence.",
      keyPoints: [
        { title: "Polyvalence", body: "La taille qui couvre le plus grand nombre de morphologies." },
        { title: "Progressifs", body: "Hauteur de verre suffisante pour loger les trois zones de vision." },
        { title: "Disponibilité", body: "C'est la taille la plus représentée dans notre stock." },
      ],
      advice:
        "Même en taille standard, l'ajustement final (plaquettes, longueur des branches) change tout : il est gratuit et se fait en cinq minutes en boutique.",
      faq: [
        { q: "La taille moyenne convient-elle à tout le monde ?", a: "À la plupart des adultes. La mesure exacte de la largeur du visage est prise gratuitement lors du test de vue." },
        { q: "Peut-on ajuster une monture standard à un visage étroit ?", a: "En partie, via les plaquettes et les branches. Au-delà, une taille inférieure sera plus confortable." },
      ],
    },
    en: {
      suitsTitle: "Who are medium frames for?",
      suits:
        "This is the standard size, the one that suits most adult faces. It gives the best margin for progressive lenses and stays balanced in photos and on video calls.",
      keyPoints: [
        { title: "Versatility", body: "The size that covers the widest range of face shapes." },
        { title: "Progressives", body: "Enough lens height to fit the three vision zones." },
        { title: "Availability", body: "It is the most represented size in our stock." },
      ],
      advice:
        "Even in a standard size, the final fitting (nose pads, temple length) changes everything: it is free and takes five minutes in store.",
      faq: [
        { q: "Does the medium size suit everyone?", a: "Most adults. The exact face width is measured for free during the eye test." },
        { q: "Can a standard frame be adjusted to a narrow face?", a: "Partly, via pads and temples. Beyond that, a smaller size will be more comfortable." },
      ],
    },
  },

  large: {
    related: [
      { key: "shape", valueId: "square" },
      { key: "gender", valueId: "men" },
      { key: "material", valueId: "tr" },
    ],
    fr: {
      suitsTitle: "À qui conviennent les grandes montures ?",
      suits:
        "Les grandes montures conviennent aux visages larges et aux porteurs de verres progressifs, qui ont besoin d'une hauteur de verre importante. Elles offrent aussi un champ de vision plus large en vision de loin.",
      keyPoints: [
        { title: "Champ de vision", body: "Plus de surface de verre, donc moins de zones aveugles sur les côtés." },
        { title: "Progressifs", body: "La hauteur disponible facilite le passage entre les trois zones de vision." },
        { title: "Confort", body: "Sur un visage large, une grande monture évite la compression des tempes." },
      ],
      advice:
        "Une grande monture doit rester légère : préférez le TR90 ou un acétate fin si vous la portez toute la journée.",
      faq: [
        { q: "Une grande monture est-elle plus lourde ?", a: "Pas nécessairement : la matière compte davantage que la taille. Le TR90 reste très léger même en grand format." },
        { q: "Les grandes montures conviennent-elles aux fortes corrections ?", a: "Elles augmentent l'épaisseur des verres. Un cercle épais en acétate permet de la masquer efficacement." },
      ],
    },
    en: {
      suitsTitle: "Who are large frames for?",
      suits:
        "Large frames suit wide faces and progressive lens wearers, who need significant lens height. They also give a wider field of view at distance.",
      keyPoints: [
        { title: "Field of view", body: "More lens area means fewer blind zones at the sides." },
        { title: "Progressives", body: "Available height makes moving between the three vision zones easier." },
        { title: "Comfort", body: "On a wide face, a large frame avoids compressing the temples." },
      ],
      advice:
        "A large frame must stay light: prefer TR90 or thin acetate if you wear it all day.",
      faq: [
        { q: "Are large frames heavier?", a: "Not necessarily: material matters more than size. TR90 stays very light even in a large format." },
        { q: "Do large frames work with strong prescriptions?", a: "They increase lens thickness. A thick acetate rim hides it effectively." },
      ],
    },
  },
};
